import * as React from 'react';
import { Form, Formik } from 'formik';
import propz from 'propz';
import { Loader } from 'Src/components/Loader/Loader';
import { Switch } from 'Src/components/Switch/Switch';
import { updateSchoolAdvancedSettings } from 'Src/helpers/service/admin/settings';
import { AppUser } from 'Src/views/App/App';
import { School } from 'Src/models/school';
import { SchoolSettingsTabs } from 'Src/views/GenericView/AdminView/AdminConsole/SchoolSettings/SchoolSettingsTabs';
import 'Src/views/GenericView/AdminView/AdminConsole/SchoolSettingsAdvanced/SchoolSettingsAdvanced.scss';

const DEFAULTS = {
  enabled: true,
  schoolInitiation: 'ALL_CONNECTED',
  announcementsEnabled: true,
  announcementAudiences: ['PARTICIPANTS', 'WAITING_LIST', 'INVITED', 'ALL_ENGAGED'],
  autoCloseDaysAfterEnd: 30
};

export const UnionTournamentCommunicationsSettings: React.FC<{
  user: AppUser;
  onSchoolUpdate: () => Promise<School>;
}> = ({ user, onSchoolUpdate }) => {
  const [loading, setLoading] = React.useState(false);
  const available = propz.get(user, ['activeSchool', 'tournamentCommunicationsAvailable'], true) !== false;
  const saved = propz.get(user, ['activeSchool', 'settings', 'tournamentComms'], {});
  const initialValues = { tournamentComms: { ...DEFAULTS, ...saved } };
  if (loading) return <Loader />;
  const Row = ({ title, hint, children }) => (
    <div className="schoolSettingsToggleRow">
      <div className="schoolSettingsToggleRow__copy">
        <div className="schoolSettingsToggleRow__title">{title}</div>
        <div className="schoolSettingsToggleRow__hint">{hint}</div>
      </div>
      {children}
    </div>
  );
  return (
    <div className="schoolSettingsPage schoolSettingsAdvancedPage eventCommunicationsSettingsPage">
      <header className="schoolSettingsHeader">
        <h1>Union settings</h1>
        <SchoolSettingsTabs isUnion />
      </header>
      {!available ? (
        <section className="eventCommunicationsEntitlement">
          <span>✦</span>
          <div>
            <h2>Tournament Communications is not enabled</h2>
            <p>Contact Squad In Touch to enable secure chats between this union and tournament schools.</p>
          </div>
          <a href="mailto:support@squadintouch.com?subject=Enable%20Tournament%20Communications">Contact support</a>
        </section>
      ) : null}
      {available ? (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={async values => {
            setLoading(true);
            try {
              await updateSchoolAdvancedSettings(user, values);
              await onSchoolUpdate();
            } finally {
              setLoading(false);
            }
          }}
        >
          {({ values, setFieldValue, dirty, resetForm }) => (
            <Form>
              <section className="schoolSettingsCard schoolSettingsAdvancedCard eventCommunicationsSettingsCard is-shared">
                <div className="schoolSettingsCard__header">
                  <div>
                    <h3>Tournament chat defaults</h3>
                    <p>New and existing tournaments use these values unless a tournament has an explicit override.</p>
                  </div>
                </div>
                <div className="schoolSettingsToggleRows">
                  <Row
                    title="Chats enabled by default"
                    hint="Organisers can still turn chats off for an individual tournament."
                  >
                    <Switch
                      name="tournament-default-enabled"
                      checked={values.tournamentComms.enabled}
                      onChange={event => setFieldValue('tournamentComms.enabled', event.target.checked)}
                    />
                  </Row>
                  <Row
                    title="Who can start a private chat?"
                    hint="The organiser can always initiate while chats are enabled."
                  >
                    <div className="schoolSettingsSelect">
                      <select
                        value={values.tournamentComms.schoolInitiation}
                        onChange={event => setFieldValue('tournamentComms.schoolInitiation', event.target.value)}
                      >
                        <option value="ORGANISER_ONLY">Organiser only</option>
                        <option value="PARTICIPANTS">Confirmed participants</option>
                        <option value="PARTICIPANTS_WAITING_LIST">Participants and waiting list</option>
                        <option value="ALL_CONNECTED">Participants, waiting list and invited schools</option>
                      </select>
                    </div>
                  </Row>
                  <Row title="Organiser announcements" hint="Allow read-only updates to selected school audiences.">
                    <Switch
                      name="tournament-default-announcements"
                      checked={values.tournamentComms.announcementsEnabled}
                      onChange={event => setFieldValue('tournamentComms.announcementsEnabled', event.target.checked)}
                    />
                  </Row>
                  <Row title="Announcement audiences" hint="Choose which audience options organisers can use.">
                    <div className="schoolSettingsSelect">
                      {[
                        ['PARTICIPANTS', 'Confirmed participants'],
                        ['WAITING_LIST', 'Waiting list'],
                        ['INVITED', 'Invited schools'],
                        ['ALL_ENGAGED', 'All connected schools']
                      ].map(([value, label]) => (
                        <label key={value} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                          <input
                            type="checkbox"
                            checked={values.tournamentComms.announcementAudiences.includes(value)}
                            onChange={event =>
                              setFieldValue(
                                'tournamentComms.announcementAudiences',
                                event.target.checked
                                  ? [...values.tournamentComms.announcementAudiences, value]
                                  : values.tournamentComms.announcementAudiences.filter(item => item !== value)
                              )
                            }
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </Row>
                  <Row
                    title="Close conversations after the tournament"
                    hint="Closed conversations remain available as safeguarding history."
                  >
                    <div className="schoolSettingsSelect">
                      <select
                        value={values.tournamentComms.autoCloseDaysAfterEnd}
                        onChange={event =>
                          setFieldValue('tournamentComms.autoCloseDaysAfterEnd', Number(event.target.value))
                        }
                      >
                        <option value={0}>Never</option>
                        <option value={1}>After 1 day</option>
                        <option value={7}>After 7 days</option>
                        <option value={14}>After 14 days</option>
                        <option value={30}>After 30 days</option>
                        <option value={90}>After 90 days</option>
                      </select>
                    </div>
                  </Row>
                </div>
              </section>
              {dirty ? (
                <div className="schoolSettingsSaveBar eventCommunicationsSettingsSaveBar">
                  <span className="schoolSettingsSaveBar__status">You have unsaved changes</span>
                  <div className="schoolSettingsSaveBar__actions">
                    <button
                      type="button"
                      className="schoolSettingsButton schoolSettingsButton--secondary"
                      onClick={() => resetForm()}
                    >
                      Discard
                    </button>
                    <button type="submit" className="schoolSettingsButton schoolSettingsButton--primary">
                      Save tournament chat defaults
                    </button>
                  </div>
                </div>
              ) : null}
            </Form>
          )}
        </Formik>
      ) : null}
    </div>
  );
};
